import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button, Modal, Form, Spinner } from "react-bootstrap";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { globalErrorHandler } from "../../../utils/GlobalErrorHandler";

const DocumentsList = () => {
  const [documents, setDocuments] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadTitle, setUploadTitle] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [selectedDocId, setSelectedDocId] = useState(null);
  const navigate = useNavigate();

  // Scroll to top on load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Fetch documents
  const fetchDocuments = () => {
    axios
      .get(`${process.env.REACT_APP_WEB_SERVICES_URL}/admin/get-documents`, {
        withCredentials: true,
      })
      .then((res) => {
        if (res.data.status) setDocuments(res.data.documents);
      })
      .catch((err) => globalErrorHandler(err, navigate));
  };

  useEffect(() => {
    fetchDocuments();
  }, [navigate]);

  // Handle download
  const handleDownload = (id) => {
    window.open(
      `${process.env.REACT_APP_WEB_SERVICES_URL}/admin/download-document/${id}`,
      "_blank"
    );
  };

  // Open upload modal
  const openUploadModal = (docId = null, title = "") => {
    setSelectedDocId(docId);
    setUploadTitle(title || "");
    setUploadFile(null);
    setShowModal(true);
  };

  // Handle upload submit
  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    if (!uploadFile) {
      Swal.fire("Warning", "Please select a file to upload.", "warning");
      return;
    }

    const formData = new FormData();
    formData.append("file", uploadFile);
    formData.append("title", uploadTitle);
    if (selectedDocId) formData.append("id", selectedDocId);

    setIsUploading(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_WEB_SERVICES_URL}/admin/upload-document`,
        formData,
        {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (res.data.status) {
        Swal.fire("Success", res.data.message, "success");
        setShowModal(false);
        fetchDocuments();
      } else {
        Swal.fire("Error", res.data.message, "error");
      }
    } catch (err) {
      globalErrorHandler(err, navigate);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="container-fluid px-4 mt-4">
      <div className="row mb-3 align-items-center">
        <div className="col">
          <h3>Documents List</h3>
        </div>
        <div className="col text-end">
          <Button variant="success" onClick={() => openUploadModal()}>
            <i className="bi bi-upload me-1"></i> Upload New Document
          </Button>
        </div>
      </div>

      <div className="card shadow mt-4">
        <div className="card-body p-4">
          <div className="table-responsive">
            <table className="table table-bordered table-hover shadow-sm nowrap">
              <thead className="table-success">
                <tr>
                  <th>#</th>
                  <th>Title</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {documents.map((doc, idx) => (
                  <tr key={doc.id}>
                    <td>{idx + 1}</td>
                    <td>{doc.title}</td>
                    <td>
                      <Button
                        variant="secondary"
                        size="sm"
                        className="me-1"
                        onClick={() => handleDownload(doc.id)}
                      >
                        <i className="bi bi-download me-1"></i> Download
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => openUploadModal(doc.id, doc.title)}
                      >
                        <i className="bi bi-upload me-1"></i> Upload
                      </Button>
                    </td>
                  </tr>
                ))}
                {documents.length === 0 && (
                  <tr>
                    <td colSpan="3" className="text-center">
                      No documents found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Upload Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedDocId ? "Update Document" : "Upload New Document"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleUploadSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Document Title</Form.Label>
              <Form.Control
                type="text"
                value={uploadTitle}
                onChange={(e) => setUploadTitle(e.target.value)}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Select File</Form.Label>
              <Form.Control
                type="file"
                onChange={(e) => setUploadFile(e.target.files[0])}
                accept=".pdf,.doc,.docx,.jpg,.png,.xlsx"
                required
              />
              {selectedDocId && (
    <Form.Text className="text-danger">
    <strong> Selecting a new file will override the existing document</strong>.
    </Form.Text>
  )}
            </Form.Group>
            <div className="text-end">
              <Button
                variant="secondary"
                className="me-2"
                onClick={() => setShowModal(false)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={isUploading}
              >
                {isUploading ? (
                  <>
                    <Spinner
                      animation="border"
                      size="sm"
                      className="me-2"
                    />
                    Uploading...
                  </>
                ) : (
                  "Upload"
                )}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default DocumentsList;
